<template>
    <div>
        <re-b-modal
            ref="modal"
            :header="$t('signature.captureTitle')"
            large
            hide-footer
            :no-close-on-backdrop="true"
            @show="onOpen"
            @hide="onHide">
            <div class="sig-qr-wrap">
                <div v-if="qrLoading" class="sig-qr-loading">
                    <i class="fa fa-fw fa-spinner fa-spin"></i>
                </div>
                <img v-else-if="qrDataUrl" :src="qrDataUrl" class="sig-qr-img" alt="QR"/>
                <p class="sig-qr-tip">{{ $t('signature.qrTip') }}</p>
                <b-button
                    class="sig-open-btn"
                    variant="primary"
                    :disabled="qrLoading || !qrUuid"
                    @click="openInlineSign">
                    <i class="fa fa-fw fa-signature"></i>
                    {{ $t('signature.openSign') }}
                </b-button>
                <p v-if="qrPolling" class="sig-qr-status">
                    <i class="fa fa-fw fa-spinner fa-spin"></i> {{ $t('signature.qrWaiting') }}
                </p>
                <b-button
                    class="sig-refresh-btn"
                    variant="outline-primary"
                    size="sm"
                    :disabled="qrLoading"
                    @click="initQr">
                    <i v-if="qrLoading" class="fa fa-fw fa-spinner fa-spin"></i>
                    {{ $t('signature.qrRefresh') }}
                </b-button>
            </div>
        </re-b-modal>

        <!--
            本机签字：整屏覆盖的签字板。
            早先是 window.open 开新标签页跑 MobileSignPage，移动端浏览器会拦新标签页，
            签完也未必关得掉；改成同页覆盖后不再依赖弹窗权限，也没有"回不去原页面"的问题。
            扫码签字的链路（MobileSignPage）保持不变，供客户用自己的手机签。
        -->
        <div ref="inlineOverlay" v-show="inlineSignVisible" class="sig-inline-overlay">
            <div class="sig-inline-head">
                <span class="sig-inline-title">{{ $t('signature.captureTitle') }}</span>
                <b-button size="sm" variant="outline-secondary" @click="closeInlineSign">
                    <i class="fa fa-fw fa-times"></i>
                    {{ $t('signature.inlineClose') }}
                </b-button>
            </div>
            <div class="sig-inline-body">
                <!-- v-if 而非 v-show：签字板要在容器已经有实际尺寸时才 mount，否则 canvas 会被按 0 尺寸初始化 -->
                <mobile-signature-pad
                    v-if="inlineSignVisible"
                    :uuid="qrUuid"
                    :token="qrToken"
                    @signed="onInlineSigned"/>
            </div>
        </div>
    </div>
</template>

<script>
import QRCode from "qrcode";
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal";
import MobileSignaturePad from "@/components/signature/MobileSignaturePad.vue";
import { prepareSignature, getSignature } from "@/utils/signatureApi";

const POLL_INTERVAL = 3000;
const CROSS_TAB_KEY = "mmf_signature_done";

export default {
    name: "SignatureCaptureDialog",
    components: { ReBModal, MobileSignaturePad },
    props: {
        visible: { type: Boolean, default: false }
    },
    data() {
        return {
            innerVisible: this.visible,
            qrLoading: false,
            qrDataUrl: null,
            qrUuid: null,
            qrToken: null,
            signUrl: null,
            qrPolling: false,
            pollTimer: null,
            signaled: false,
            inlineSignVisible: false
        };
    },
    watch: {
        visible(val) {
            if (val) {
                this.$nextTick(() => this.$refs.modal && this.$refs.modal.showModal());
            } else if (this.$refs.modal) {
                this.$refs.modal.hide();
            }
        }
    },
    mounted() {
        // 覆盖层挂到 body 下：b-modal 自己也是 portal 到 body 的，留在组件原位会受宿主页面
        // 任意一层 stacking context 影响，可能被弹窗盖住。
        if (this.$refs.inlineOverlay && document.body) {
            document.body.appendChild(this.$refs.inlineOverlay);
        }
    },
    methods: {
        onOpen() {
            this.innerVisible = true;
            this.signaled = false;
            window.addEventListener("storage", this.onCrossTabSigned);
            document.addEventListener("visibilitychange", this.onVisible);
            this.initQr();
        },
        onHide() {
            this.innerVisible = false;
            this.inlineSignVisible = false;
            this.stopPolling();
            window.removeEventListener("storage", this.onCrossTabSigned);
            document.removeEventListener("visibilitychange", this.onVisible);
            this.qrDataUrl = null;
            this.qrUuid = null;
            this.qrToken = null;
            this.signUrl = null;
            this.$emit("update:visible", false);
        },
        onCrossTabSigned(e) {
            // 扫码签字若恰好开在同一浏览器的另一个标签页，localStorage 通知比轮询更快；
            // 后台标签页的定时器会被浏览器节流，这条通道是兜底。
            if (e.key !== CROSS_TAB_KEY || !e.newValue) {
                return;
            }
            try {
                const payload = JSON.parse(e.newValue);
                if (payload && payload.uuid && payload.uuid === this.qrUuid) {
                    localStorage.removeItem(CROSS_TAB_KEY);
                    this.onSigned(payload.uuid);
                }
            } catch (err) {
                // 忽略无法解析的通知
            }
        },
        onVisible() {
            // 回到本标签页时立即拉取一次，兜底后台被节流/冻结的轮询
            if (document.visibilityState === "visible" && this.qrUuid && !this.signaled) {
                this.pollOnce();
            }
        },
        pollOnce() {
            if (!this.qrUuid) {
                return;
            }
            getSignature(this.qrUuid)
                .then(res => {
                    if (res && res.signatureUuid) {
                        this.onSigned(res.signatureUuid);
                    }
                })
                .catch(() => {
                    // 尚未完成签名，继续等待
                });
        },
        initQr() {
            this.stopPolling();
            this.inlineSignVisible = false;
            this.qrLoading = true;
            this.qrDataUrl = null;
            this.signUrl = null;
            prepareSignature()
                .then(res => {
                    this.qrUuid = res.uuid;
                    this.qrToken = res.token;
                    const resolved = this.$router.resolve({
                        name: "mobileSign",
                        query: { uuid: this.qrUuid, token: this.qrToken }
                    });
                    this.signUrl = window.location.origin + resolved.href;
                    return QRCode.toDataURL(this.signUrl, { width: 240, margin: 1 });
                })
                .then(dataUrl => {
                    this.qrDataUrl = dataUrl;
                    this.startPolling();
                })
                .catch(() => {
                    this.$message.error(this.$t("signature.qrGenerateFailed"));
                })
                .finally(() => {
                    this.qrLoading = false;
                });
        },
        openInlineSign() {
            if (!this.qrUuid) {
                return;
            }
            this.inlineSignVisible = true;
        },
        closeInlineSign() {
            this.inlineSignVisible = false;
        },
        onInlineSigned(result) {
            this.inlineSignVisible = false;
            this.onSigned((result && result.uuid) || this.qrUuid);
        },
        startPolling() {
            this.stopPolling();
            if (!this.qrUuid) {
                return;
            }
            this.qrPolling = true;
            this.pollTimer = setInterval(() => this.pollOnce(), POLL_INTERVAL);
        },
        stopPolling() {
            this.qrPolling = false;
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        onSigned(uuid) {
            if (this.signaled) {
                return;
            }
            this.signaled = true;
            this.stopPolling();
            this.$emit("signed", { uuid: uuid });
            if (this.$refs.modal) {
                this.$refs.modal.hide();
            }
        }
    },
    beforeDestroy() {
        this.stopPolling();
        window.removeEventListener("storage", this.onCrossTabSigned);
        document.removeEventListener("visibilitychange", this.onVisible);
        // 覆盖层已被搬到 body 下，父组件销毁时 Vue 不会替我们摘掉它
        const overlay = this.$refs.inlineOverlay;
        if (overlay && overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
        }
    }
};
</script>

<style scoped>
.sig-qr-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 12px 0;
}
.sig-qr-loading {
    width: 240px;
    height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    color: #1E5274;
}
.sig-qr-img {
    width: 240px;
    height: 240px;
}
.sig-qr-tip {
    color: #5b6b7a;
    font-size: 13px;
    margin: 0;
}
.sig-qr-status {
    color: #1E5274;
    font-size: 13px;
    margin: 0;
}
.sig-open-btn {
    font-weight: 500;
}
.sig-refresh-btn {
    color: #1E5274;
    border-color: #1E5274;
    font-weight: 500;
}
.sig-refresh-btn:hover,
.sig-refresh-btn:focus {
    color: #fff;
    background-color: #1E5274;
    border-color: #1E5274;
    box-shadow: 0 2px 6px rgba(30, 82, 116, 0.25);
}

/* z-index 要压过 b-modal(1050) 与其遮罩(1040)：二维码弹窗仍开在下面 */
.sig-inline-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3000;
    background: #f5f6f8;
    display: flex;
    flex-direction: column;
}
.sig-inline-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px;
    background: #fff;
    border-bottom: 1px solid #e7edf3;
}
.sig-inline-title {
    font-size: 16px;
    font-weight: bold;
    color: #1E5274;
}
.sig-inline-body {
    flex: 1;
    min-height: 0;
}
</style>
